import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-parchment-ink text-parchment-light hover:bg-parchment-ink/90 active:bg-parchment-ink/80 shadow-parchment-card',
  secondary:
    'bg-parchment-dark text-parchment-ink border border-parchment-border hover:bg-parchment-dark/80',
  ghost: 'bg-transparent text-parchment-ink hover:bg-parchment-dark/50',
  destructive:
    'bg-danger text-parchment-light hover:bg-danger-dark active:bg-danger-dark shadow-parchment-card',
  link: 'bg-transparent text-accent hover:underline underline-offset-2 p-0 h-auto',
};

// `coarse:` raises the hit area to 44px on touch input only — desktop keeps the
// denser heights.
const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm rounded-md gap-1.5 coarse:min-h-11',
  md: 'h-10 px-4 text-sm rounded-md gap-2 coarse:min-h-11',
  lg: 'h-12 px-6 text-base rounded-lg gap-2',
  icon: 'h-10 w-10 rounded-md coarse:min-h-11 coarse:min-w-11',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading,
    leftIcon,
    rightIcon,
    asChild,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const classes = cn(
    'inline-flex items-center justify-center font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-parchment-light',
    'disabled:cursor-not-allowed disabled:opacity-50',
    variantStyles[variant],
    sizeStyles[size],
    className,
  );

  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || isLoading}
      className={classes}
      {...rest}
    >
      {isLoading ? <Spinner className="h-4 w-4" /> : leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
});
